import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import MemberAvatar from '@/components/MemberAvatar';
import { SponsorBanner } from '@/components/SponsorBanner';
import RichText from '@/components/RichText';
import usePageMeta from '@/hooks/usePageMeta';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  User, MapPin, Briefcase, Building2, Tag, ExternalLink, Linkedin, Handshake,
  GraduationCap, Award, Sparkles, Target, Lightbulb, Users as UsersIcon,
  MessageSquare, FlaskConical, Calendar, BookOpen, ChevronDown, ChevronUp,
  ArrowLeft,
} from 'lucide-react';

const BACKEND_URL = '';

const fmtDate = (iso, opts = { day: 'numeric', month: 'short', year: 'numeric' }) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', opts); } catch { return iso; }
};

const Section = ({ icon: Icon, title, count, children, testid }) => (
  <Card className="p-5 lg:p-6" data-testid={testid}>
    <h2 className="text-base font-bold font-heading text-slate-800 mb-4 flex items-center gap-2">
      <Icon className="h-5 w-5 text-brand-navy" />
      {title}
      {count != null && (
        <span className="text-xs font-medium text-slate-400 ml-1">{count}</span>
      )}
    </h2>
    {children}
  </Card>
);

const Chip = ({ children, color = 'navy' }) => {
  const palette = {
    navy: 'bg-brand-navy/5 text-brand-navy border-brand-navy/15',
    sage: 'bg-brand-sage/10 text-brand-sage border-brand-sage/30',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <span className={`inline-block text-xs px-2.5 py-1 rounded-full border ${palette[color]}`}>{children}</span>
  );
};

const ChipGroup = ({ items, color, emptyHint }) => (
  items?.length > 0 ? (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, idx) => <Chip key={idx} color={color}>{it}</Chip>)}
    </div>
  ) : (
    emptyHint ? <p className="text-xs text-slate-400 italic">{emptyHint}</p> : null
  )
);

export default function PublicProfilePage({ user: userProp }) {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  usePageMeta(profile ? {
    title: profile.name || 'Member',
    description: profile.profile?.bio?.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 200) || (profile.profile?.role && profile.profile?.organisation ? `${profile.profile.role} at ${profile.profile.organisation}` : ''),
    image: profile.picture,
  } : { title: 'Member profile' });
  const [loading, setLoading] = useState(true);
  const [bioExpanded, setBioExpanded] = useState(false);
  // If route isn't protected, fall back to cached session user so logged-in members still see navigation
  const cachedUser = (() => {
    try { return JSON.parse(sessionStorage.getItem('sll_user') || localStorage.getItem('sll_user') || 'null'); }
    catch { return null; }
  })();
  const user = userProp || cachedUser;
  const isOwnProfile = user?.user_id === userId;

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/members/public/${userId}`)
      .then(async res => { if (res.ok) setProfile(await res.json()); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
    </div>
  );

  if (!profile) return (
    <div className="min-h-screen bg-slate-50">
      {user && <Navigation user={user} />}
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <Card className="p-8 text-center max-w-sm">
          <User className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">This profile is unavailable.</p>
          <Link to="/members"><Button variant="outline" size="sm">Back to members</Button></Link>
        </Card>
      </div>
    </div>
  );

  const fullLocation = [profile.city, profile.region, profile.country].filter(Boolean).join(', ') || profile.location;
  const showBioToggle = profile.bio && profile.bio.length > 280;
  // For rich-text bios we keep the full HTML and rely on CSS line-clamp when
  // collapsed — slicing the raw string would break tags.
  const bioRendered = profile.bio;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-profile-page">
      {user && <Navigation user={user} />}

      {/* Padding-top clears the fixed Navigation (z-50). Without it the
          banner + avatar were rendering UNDERNEATH the nav on logged-in
          views, making the avatar appear to be overlapped by the banner. */}
      <div className={user ? 'pt-20 lg:pt-24' : ''}>
        {/* Header banner */}
        <div className="relative">
          {profile.header_image ? (
            <img src={profile.header_image} alt="" className="w-full h-44 lg:h-56 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : null}
          <div className={`${profile.header_image ? 'absolute inset-0' : 'h-44 lg:h-56'} bg-gradient-to-r from-brand-navy/85 via-brand-navy/40 to-brand-sage/40`} />
        </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 -mt-20 lg:-mt-24 pb-12 relative z-10">
        {/* Top card */}
        <Card className="p-6 lg:p-8 mb-6 relative z-10" data-testid="public-profile-header">
          <div className="flex flex-col lg:flex-row lg:items-end gap-5">
            {/* Avatar — relative + z-20 so it rises above the banner above */}
            <div className="relative z-20 -mt-16 lg:-mt-20">
              <MemberAvatar
                name={profile.name}
                picture={profile.picture}
                size={128}
                className="border-4 border-white shadow-lg bg-white lg:!w-32 lg:!h-32"
              />
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-bold font-heading text-slate-800" data-testid="public-profile-name">
                {profile.name}
              </h1>
              {profile.role && (
                <p className="text-base text-brand-sage font-semibold mt-0.5" data-testid="public-profile-role">{profile.role}</p>
              )}
              {profile.organisation && !profile.company && (
                <p className="text-sm text-slate-500 mt-0.5">{profile.organisation}</p>
              )}

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-3">
                {fullLocation && (
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{fullLocation}</span>
                )}
                {profile.sector && (
                  <span className="inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{profile.sector}</span>
                )}
                {profile.years_experience != null && profile.years_experience !== '' && (
                  <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />{profile.years_experience} yrs experience</span>
                )}
                {profile.created_at && (
                  <span className="inline-flex items-center gap-1.5">Member since {fmtDate(profile.created_at, { month: 'short', year: 'numeric' })}</span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {profile.linkedin_url && (
                <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm bg-[#0a66c2]/10 text-[#0a66c2] hover:bg-[#0a66c2]/15 transition-colors"
                  data-testid="public-profile-linkedin">
                  <Linkedin className="h-4 w-4" /> LinkedIn
                </a>
              )}
              {user && !isOwnProfile && (
                <Link to={`/messages?to=${profile.user_id}`}>
                  <Button size="sm" className="bg-brand-navy hover:bg-brand-navy/90 text-white" data-testid="public-profile-message-btn">
                    <MessageSquare className="h-4 w-4 mr-1.5" /> Message
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Company / Partner pills */}
          {profile.company && (
            <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100">
              <Link to={`/companies/${profile.company.company_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
                data-testid="public-company-pill">
                <Building2 className="h-3.5 w-3.5" /> {profile.company.name}
              </Link>
              {profile.company.is_partner && (
                <Link to={`/partners/partner_from_${profile.company.company_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 transition-colors"
                  data-testid="public-partner-pill">
                  <Handshake className="h-3.5 w-3.5" /> Partner profile
                </Link>
              )}
            </div>
          )}
        </Card>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* LEFT — About / Networking / Activity */}
          <div className="space-y-6 lg:col-span-2">
            {/* About */}
            <Section icon={User} title="About" testid="section-about">
              {profile.tagline && (
                <p className="text-sm text-slate-700 italic mb-3">"{profile.tagline}"</p>
              )}
              {profile.bio ? (
                <>
                  <div className={`text-sm text-slate-600 leading-relaxed ${showBioToggle && !bioExpanded ? 'line-clamp-4' : ''}`} data-testid="public-profile-bio">
                    <RichText html={bioRendered} />
                  </div>
                  {showBioToggle && (
                    <button
                      onClick={() => setBioExpanded(v => !v)}
                      data-testid="public-profile-bio-toggle"
                      className="mt-2 text-xs font-medium text-brand-navy hover:text-brand-navy/80 inline-flex items-center gap-1"
                    >
                      {bioExpanded ? <>Show less <ChevronUp className="h-3.5 w-3.5" /></> : <>Read more <ChevronDown className="h-3.5 w-3.5" /></>}
                    </button>
                  )}
                </>
              ) : (
                <p className="text-xs text-slate-400 italic">This member hasn&apos;t added a bio yet.</p>
              )}

              {profile.self_descriptions?.length > 0 ? (
                <div className="mt-3 px-3 py-2 bg-slate-50 rounded-md border border-slate-100">
                  <span className="text-xs text-slate-500 font-semibold mr-2">Describes themselves as:</span>
                  <div className="inline-flex flex-wrap gap-1.5 mt-1">
                    {profile.self_descriptions.map(d => (
                      <span key={d} className="text-xs px-2 py-0.5 rounded-full bg-brand-navy/5 text-brand-navy border border-brand-navy/15">{d}</span>
                    ))}
                  </div>
                </div>
              ) : profile.self_description && (
                <p className="text-xs text-slate-500 mt-3 px-3 py-2 bg-slate-50 rounded-md border border-slate-100">
                  <span className="font-semibold text-slate-700">Describes themselves as:</span> {profile.self_description}
                </p>
              )}
            </Section>

            {/* Recent activity */}
            <Section icon={Sparkles} title="Recent activity" testid="section-activity">
              <div className="space-y-5">
                {/* Forum posts */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5" /> Latest forum posts
                  </h3>
                  {profile.recent_posts?.length > 0 ? (
                    <ul className="space-y-2">
                      {profile.recent_posts.map(p => (
                        <li key={p.post_id} className="text-sm text-slate-700">
                          <Link to={`/forum?post=${p.post_id}`} className="hover:text-brand-navy hover:underline">
                            {p.title || '(Untitled post)'}
                          </Link>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {p.topic && <span>{p.topic} &middot; </span>}
                            {fmtDate(p.created_at)} &middot; {p.like_count} likes &middot; {p.comment_count} comments
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-xs text-slate-400 italic">No posts yet.</p>}
                </div>

                {/* BriteLabs */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                    <FlaskConical className="h-3.5 w-3.5" /> Member of {profile.britelabs?.length || 0} BriteLab{profile.britelabs?.length === 1 ? '' : 's'}
                  </h3>
                  {profile.britelabs?.length > 0 ? (
                    <ul className="space-y-1.5">
                      {profile.britelabs.map(l => (
                        <li key={l.lab_id} className="text-sm">
                          <Link to={`/britelabs/${l.lab_id}`} className="text-slate-700 hover:text-brand-navy hover:underline" data-testid={`britelab-link-${l.lab_id}`}>
                            {l.title}
                          </Link>
                          <span className="ml-2 text-[11px] text-slate-400">{l.category} &middot; {l.status}</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-xs text-slate-400 italic">Not yet contributing to any BriteLab.</p>}
                </div>

                {/* Upcoming events */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" /> Attending {profile.upcoming_events?.length || 0} upcoming event{profile.upcoming_events?.length === 1 ? '' : 's'}
                  </h3>
                  {profile.upcoming_events?.length > 0 ? (
                    <ul className="space-y-1.5">
                      {profile.upcoming_events.map(e => (
                        <li key={e.event_id} className="text-sm">
                          <Link to={`/events?event=${e.event_id}`} className="text-slate-700 hover:text-brand-navy hover:underline" data-testid={`event-link-${e.event_id}`}>
                            {e.title}
                          </Link>
                          <span className="ml-2 text-[11px] text-slate-400">
                            {fmtDate(e.date)}{e.start_time ? ` · ${e.start_time}` : ''} &middot; {e.format}{e.location ? ` · ${e.location}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-xs text-slate-400 italic">No upcoming events.</p>}
                </div>

                {/* Completed courses */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                    <BookOpen className="h-3.5 w-3.5" /> Completed {profile.completed_courses?.length || 0} course{profile.completed_courses?.length === 1 ? '' : 's'}
                  </h3>
                  {profile.completed_courses?.length > 0 ? (
                    <ul className="space-y-1.5">
                      {profile.completed_courses.map(c => (
                        <li key={c.course_id} className="text-sm">
                          <Link to={`/courses/${c.course_id}`} className="text-slate-700 hover:text-brand-navy hover:underline" data-testid={`course-link-${c.course_id}`}>
                            {c.title}
                          </Link>
                          <span className="ml-2 text-[11px] text-slate-400">
                            {c.level}{c.cpd_credits ? ` · ${c.cpd_credits} CPD` : ''}{c.completed_at ? ` · ${fmtDate(c.completed_at)}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-xs text-slate-400 italic">No completed courses yet.</p>}
                </div>
              </div>
            </Section>
          </div>

          {/* RIGHT — Experience + Networking */}
          <div className="space-y-6">
            <Section icon={Briefcase} title="Experience" testid="section-experience">
              <div className="space-y-4">
                {profile.background && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Background</h3>
                    <p className="text-sm text-slate-700">{profile.background}</p>
                  </div>
                )}
                {profile.expertise?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Expertise</h3>
                    <ChipGroup items={profile.expertise} color="navy" />
                  </div>
                )}
                {profile.specialist_areas?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Specialist areas</h3>
                    <ChipGroup items={profile.specialist_areas} color="sage" />
                  </div>
                )}
                {profile.qualifications?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                      <GraduationCap className="h-3.5 w-3.5" /> Qualifications
                    </h3>
                    <ChipGroup items={profile.qualifications} color="slate" />
                  </div>
                )}
                {profile.certifications?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                      <Award className="h-3.5 w-3.5" /> Certifications
                    </h3>
                    <ChipGroup items={profile.certifications} color="amber" />
                  </div>
                )}
                {profile.projects?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Projects</h3>
                    <ChipGroup items={profile.projects} color="violet" />
                  </div>
                )}
                {!profile.background && !profile.expertise?.length && !profile.qualifications?.length && !profile.certifications?.length && (
                  <p className="text-xs text-slate-400 italic">No experience details added yet.</p>
                )}
              </div>
            </Section>

            <Section icon={UsersIcon} title="Networking" testid="section-networking">
              <div className="space-y-4">
                {profile.working_on && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Currently working on</h3>
                    <p className="text-sm text-slate-700 whitespace-pre-line" data-testid="public-profile-working-on">{profile.working_on}</p>
                  </div>
                )}
                {profile.interests?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Interests</h3>
                    <ChipGroup items={profile.interests} color="sage" />
                  </div>
                )}
                {profile.looking_for?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5" /> Looking for
                    </h3>
                    <ChipGroup items={profile.looking_for} color="navy" />
                  </div>
                )}
                {profile.challenges?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                      <Lightbulb className="h-3.5 w-3.5" /> Challenges
                    </h3>
                    <ChipGroup items={profile.challenges} color="rose" />
                  </div>
                )}
                {profile.sustainability_goals?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Sustainability goals</h3>
                    <ChipGroup items={profile.sustainability_goals} color="sage" />
                  </div>
                )}
                {profile.priorities_2026?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Priorities for 2026</h3>
                    <ChipGroup items={profile.priorities_2026} color="amber" />
                  </div>
                )}
                {profile.community_goals?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Community goals</h3>
                    <ChipGroup items={profile.community_goals} color="sky" />
                  </div>
                )}
                {profile.forum_topics_of_interest?.length > 0 && (
                  <div>
                    <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Topics of interest</h3>
                    <ChipGroup items={profile.forum_topics_of_interest} color="violet" />
                  </div>
                )}
                {!profile.working_on && !profile.interests?.length && !profile.looking_for?.length && !profile.challenges?.length && (
                  <p className="text-xs text-slate-400 italic">No networking preferences shared yet.</p>
                )}
              </div>
            </Section>
          </div>
        </div>

        {!user && (
          <div className="mt-8 text-center">
            <Link to="/login" className="text-xs text-slate-500 hover:text-brand-navy">
              <ArrowLeft className="h-3.5 w-3.5 inline mr-1" /> Sign in to message {profile.name}
            </Link>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
